import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUpdateProfile,
  useCreateLink,
  useUpdateLink,
  useDeleteLink,
  getGetMyProfileQueryKey,
  getGetMyLinksQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Linkedin,
  Globe,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Plus,
  Trash2,
  Save,
  User,
  GripVertical,
  Info,
  ChevronDown,
  CheckCircle2,
  Zap,
  Star,
  Upload,
  Palette,
} from "lucide-react";
import type {
  ProfileResponse,
  LinkResponse,
  UpdateProfileRequest,
} from "@workspace/api-client-react";
import PhoneInput from "@/components/dashboard/PhoneInput";
import { getThemeById } from "@commonqr/shared/config/themes";
import { CARD_PROFILE_TYPES } from "@commonqr/shared/config/cardProfileTypes";

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.74a4.85 4.85 0 01-1.01-.05z" />
    </svg>
  );
}
function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}
function SnapchatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.065.001c1.778-.001 5.667.453 7.682 4.09.63 1.138.53 3.08.44 4.698l-.01.187c-.003.064.04.222.082.244.274.143.756.08 1.194-.042.143-.04.283-.087.42-.112.484-.09.967.06 1.042.44.09.477-.403.898-.937 1.126-.167.071-.64.222-.864.282-.118.03-.196.126-.167.242.02.083.05.161.08.245.368 1.011 1.076 1.827 1.838 2.455.368.303.762.575 1.183.778.27.13.358.444.21.688-.174.288-.604.445-1.018.517-.16.027-.39.067-.67.13-.396.09-.69.25-.815.565-.1.255-.042.504.147.722.085.1.145.197.145.263 0 .3-.375.633-1.11.923-.63.249-1.41.42-2.37.52-.112.013-.199.1-.227.217-.082.36-.358.62-.74.76-.416.151-.877.22-1.312.22-.435 0-.896-.07-1.313-.22-.383-.14-.658-.4-.74-.76-.028-.117-.115-.204-.227-.217-.96-.1-1.74-.271-2.37-.52-.735-.29-1.11-.622-1.11-.923 0-.066.06-.163.145-.264.19-.217.247-.466.147-.72-.125-.315-.42-.475-.816-.565-.28-.063-.51-.103-.67-.13-.414-.072-.844-.229-1.018-.517-.148-.244-.06-.558.21-.688.421-.203.815-.475 1.183-.778.762-.628 1.47-1.444 1.838-2.455.03-.084.06-.162.08-.245.029-.116-.049-.212-.167-.242-.224-.06-.697-.21-.864-.282-.534-.228-1.027-.65-.937-1.126.075-.38.558-.53 1.042-.44.137.025.277.072.42.112.438.122.92.185 1.194.042.042-.022.085-.18.082-.244l-.01-.187c-.09-1.618-.19-3.56.44-4.698C6.397.453 10.287-.001 12.065.001z" />
    </svg>
  );
}
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

const BUILTIN_PLATFORMS = [
  {
    id: "phone",
    name: "Phone / Call",
    icon: Phone,
    color: "bg-green-500",
    isPhone: true,
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    icon: WhatsAppIcon,
    color: "bg-[#25D366]",
    isPhone: true,
  },
  {
    id: "instagram",
    name: "Instagram",
    icon: Instagram,
    color: "bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600",
    isPhone: false,
    placeholder: "https://instagram.com/yourusername",
  },
  {
    id: "facebook",
    name: "Facebook",
    icon: Facebook,
    color: "bg-[#1877F2]",
    isPhone: false,
    placeholder: "https://facebook.com/yourpage",
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    icon: Linkedin,
    color: "bg-[#0A66C2]",
    isPhone: false,
    placeholder: "https://linkedin.com/in/yourname",
  },
  {
    id: "youtube",
    name: "YouTube",
    icon: Youtube,
    color: "bg-[#FF0000]",
    isPhone: false,
    placeholder: "https://youtube.com/@yourchannel",
  },
  {
    id: "tiktok",
    name: "TikTok",
    icon: TikTokIcon,
    color: "bg-black",
    isPhone: false,
    placeholder: "https://tiktok.com/@yourusername",
  },
  {
    id: "twitter",
    name: "X (Twitter)",
    icon: Twitter,
    color: "bg-black",
    isPhone: false,
    placeholder: "https://x.com/yourhandle",
  },
  {
    id: "telegram",
    name: "Telegram",
    icon: TelegramIcon,
    color: "bg-[#2CA5E0]",
    isPhone: false,
    placeholder: "https://t.me/yourusername",
  },
  {
    id: "snapchat",
    name: "Snapchat",
    icon: SnapchatIcon,
    color: "bg-[#FFFC00] text-black",
    isPhone: false,
    placeholder: "https://snapchat.com/add/yourname",
  },
  {
    id: "email",
    name: "Email",
    icon: Mail,
    color: "bg-blue-500",
    isPhone: false,
    placeholder: "mailto:you@example.com",
  },
  {
    id: "website",
    name: "Website",
    icon: Globe,
    color: "bg-slate-700",
    isPhone: false,
    placeholder: "https://yourwebsite.com",
  },
  {
    id: "location",
    name: "Location / Map",
    icon: MapPin,
    color: "bg-red-500",
    isPhone: false,
    placeholder: "https://maps.google.com/?q=...",
  },
  {
    id: "payment",
    name: "Payment Link",
    icon: CreditCard,
    color: "bg-indigo-500",
    isPhone: false,
    placeholder: "https://payment.link/...",
  },
];

interface CustomLink {
  id?: number;
  label: string;
  url: string;
  isNew?: boolean;
}
interface MyProfileProps {
  profile: ProfileResponse;
  links: LinkResponse[];
  onLiveChange?: (profile: any, links: any[]) => void;
  syncThemeRef?: React.MutableRefObject<((themeId: string | null, themeImageUrl: string | null) => void) | null>;
}

function Tooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="text-muted-foreground hover:text-primary transition-colors"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {show && (
        <span className="absolute z-50 left-5 top-0 w-56 bg-background text-foreground text-xs rounded-xl px-3 py-2 shadow-xl leading-relaxed">
          {text}
        </span>
      )}
    </span>
  );
}

function getPlanLinkLimit(plan: string): number {
  if (plan === "pro_plus" || plan === "enterprise" || plan === "ent_plus")
    return 10;
  if (plan === "pro") return 5;
  return 3;
}

function getPlanLabel(plan: string): string {
  if (plan === "pro_plus" || plan === "ent_plus") return "Pro Plus: 10 links";
  if (plan === "pro") return "Pro: 5 links";
  return "Free: 3 links";
}
export default function MyProfile({ profile, links, onLiveChange, syncThemeRef }: MyProfileProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateProfile = useUpdateProfile();
  const createLink = useCreateLink();
  const updateLink = useUpdateLink();
  const deleteLink = useDeleteLink();

  const profileForm = useForm<UpdateProfileRequest>({
    defaultValues: {
      name: profile.name ?? "",
      bio: profile.bio ?? "",
      email: profile.email ?? "",
      avatarUrl: profile.avatarUrl ?? "",
      location: profile.location ?? "",
      website: profile.website ?? "",
      themeId: profile.themeId ?? null,
      themeImageUrl: profile.themeImageUrl ?? null,
    },
  });

  const linksByPlatform: Record<string, LinkResponse> = {};
  const customLinks: LinkResponse[] = [];
  links.forEach((l) => {
    if (BUILTIN_PLATFORMS.some((p) => p.id === l.platform)) {
      linksByPlatform[l.platform] = l;
    } else {
      customLinks.push(l);
    }
  });

  const [platformUrls, setPlatformUrls] = useState<Record<string, string>>(
    () => {
      const init: Record<string, string> = {};
      BUILTIN_PLATFORMS.forEach((p) => {
        init[p.id] = linksByPlatform[p.id]?.url || "";
      });
      return init;
    },
  );

  const [myCustomLinks, setMyCustomLinks] = useState<CustomLink[]>(() =>
    customLinks.map((l) => ({ id: l.id, label: l.label || "", url: l.url })),
  );

  useEffect(() => {
    if (syncThemeRef) {
      syncThemeRef.current = (themeId, themeImageUrl) => {
        profileForm.setValue("themeId", themeId);
        profileForm.setValue("themeImageUrl", themeImageUrl);
      };
    }
    return () => { if (syncThemeRef) syncThemeRef.current = null; };
  }, [syncThemeRef, profileForm]);

  const initialTheme = getThemeById(profile.themeId);
  const [selectedIndustry, setSelectedIndustry] = useState<string>(
    initialTheme?.industry ?? "Beauty & Personal Care",
  );
  const [themeIndustriesList, setThemeIndustriesList] = useState<string[]>([]);
  const [filteredThemes, setFilteredThemes] = useState<any[]>([]);

  useEffect(() => {
    fetch('/api/v1/themes/industries')
      .then(r => r.json())
      .then(d => {
        const list = d.industries ?? [];
        setThemeIndustriesList(list);
        if (!initialTheme?.industry && list.length > 0) setSelectedIndustry(list[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedIndustry) return;
    fetch(`/api/v1/themes?industry=${encodeURIComponent(selectedIndustry)}&limit=50`)
      .then(r => r.json())
      .then(d => setFilteredThemes(d.themes ?? []))
      .catch(() => {});
  }, [selectedIndustry]);
  const [isUploadingTheme, setIsUploadingTheme] = useState(false);
  const selectedThemeId = profileForm.watch("themeId");
  const themeImageUrl = profileForm.watch("themeImageUrl");

  // Live preview sync
  const watchedName = profileForm.watch("name");
  const watchedBio = profileForm.watch("bio");
  const watchedLocation = profileForm.watch("location");
  const watchedAvatarUrl = profileForm.watch("avatarUrl");

  useEffect(() => {
    if (!onLiveChange) return;
    const liveProfile = {
      name: watchedName || profile.name,
      bio: watchedBio || undefined,
      location: watchedLocation || undefined,
      avatarUrl: watchedAvatarUrl || undefined,
      username: profile.username,
      isVerified: profile.isVerified,
      scanCount: profile.scanCount ?? 0,
      themeId: selectedThemeId ?? null,
      themeImageUrl: themeImageUrl ?? null,
    };
    const liveLinks = Object.entries(platformUrls)
      .filter(([, url]) => url.trim())
      .map(([platform, url], i) => ({ id: platform, platform, label: BUILTIN_PLATFORMS.find((p) => p.id === platform)?.name ?? platform, url, sortOrder: i + 1 }));
    onLiveChange(liveProfile, liveLinks);
  }, [watchedName, watchedBio, watchedLocation, watchedAvatarUrl, selectedThemeId, themeImageUrl, platformUrls]);

  // Auto-save debounce 5s
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!profileForm.formState.isDirty) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      handleSaveAll();
    }, 5000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [watchedName, watchedBio, watchedLocation, watchedAvatarUrl, selectedThemeId, themeImageUrl]);

  const [selectedType, setSelectedType] = useState(
    (profile as any).profileType ?? "",
  );
  const [typeOpen, setTypeOpen] = useState(false);
  const [linkOrder, setLinkOrder] = useState<string[]>(() =>
    BUILTIN_PLATFORMS.map((p) => p.id),
  );

  const customLinkLimit = getPlanLinkLimit(profile.currentPlan ?? "free");

  const applyProfileType = (typeId: string) => {
    setSelectedType(typeId);
    setTypeOpen(false);
    const type = CARD_PROFILE_TYPES.find((t) => t.id === typeId);
    if (!type) return;
    const recommended = type.order.filter((id) =>
      BUILTIN_PLATFORMS.some((p) => p.id === id),
    );
    const rest = BUILTIN_PLATFORMS.map((p) => p.id).filter(
      (id) => !recommended.includes(id),
    );
    setLinkOrder([...recommended, ...rest]);
    toast({
      title: `Links sorted for ${type.label}`,
      description:
        "Your recommended link order has been applied. Drag to adjust.",
    });
  };

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const handleDragStart = (i: number) => setDragIdx(i);
  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    const newOrder = [...linkOrder];
    newOrder.splice(i, 0, newOrder.splice(dragIdx, 1)[0]);
    setLinkOrder(newOrder);
    setDragIdx(i);
  };
  const handleDragEnd = () => setDragIdx(null);

  const orderedPlatforms = linkOrder
    .map((id) => BUILTIN_PLATFORMS.find((p) => p.id === id)!)
    .filter(Boolean);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      const formValues = profileForm.getValues();
      const profileData: UpdateProfileRequest = {
        name: formValues.name || profile.name || "User",
        bio: formValues.bio || undefined,
        email: formValues.email || undefined,
        avatarUrl: formValues.avatarUrl || undefined,
        location: formValues.location || undefined,
        website: formValues.website || undefined,
        profileType: selectedType     || undefined,
        themeId: formValues.themeId ?? null,
        themeImageUrl: formValues.themeImageUrl ?? null,
      };

      await new Promise<void>((resolve, reject) => {
        updateProfile.mutate(
          { data: profileData },
          { onSuccess: () => resolve(), onError: reject },
        );
      });

      for (let idx = 0; idx < orderedPlatforms.length; idx++) {
        const platform = orderedPlatforms[idx];
        const url = platformUrls[platform.id]?.trim();
        const existing = linksByPlatform[platform.id];
        const sortOrder = idx + 1;
        if (url && !existing) {
          await new Promise<void>((resolve, reject) => {
            createLink.mutate(
              {
                data: {
                  profileId: profile.id,
                  platform: platform.id,
                  url,
                  label: platform.name,
                  sortOrder,
                },
              },
              { onSuccess: () => resolve(), onError: reject },
            );
          });
        } else if (
          url &&
          existing &&
          (url !== existing.url || sortOrder !== existing.sortOrder)
        ) {
          await new Promise<void>((resolve, reject) => {
            updateLink.mutate(
              {
                id: existing.id,
                data: {
                  profileId: profile.id,
                  platform: platform.id,
                  url,
                  label: existing.label || platform.name,
                  sortOrder,
                },
              },
              { onSuccess: () => resolve(), onError: reject },
            );
          });
        } else if (!url && existing) {
          await new Promise<void>((resolve, reject) => {
            deleteLink.mutate(
              { id: existing.id },
              { onSuccess: () => resolve(), onError: reject },
            );
          });
        }
      }

      for (const custom of myCustomLinks) {
        if (!custom.url.trim() || !custom.label.trim()) continue;
        if (custom.id) {
          const orig = customLinks.find((l) => l.id === custom.id);
          if (
            orig &&
            (orig.url !== custom.url || (orig.label || "") !== custom.label)
          ) {
            await new Promise<void>((resolve, reject) => {
              updateLink.mutate(
                {
                  id: custom.id!,
                  data: {
                    profileId: profile.id,
                    platform: "custom",
                    url: custom.url,
                    label: custom.label,
                    sortOrder: 99,
                  },
                },
                { onSuccess: () => resolve(), onError: reject },
              );
            });
          }
        } else {
          await new Promise<void>((resolve, reject) => {
            createLink.mutate(
              {
                data: {
                  profileId: profile.id,
                  platform: "custom",
                  url: custom.url,
                  label: custom.label,
                  sortOrder: 99,
                },
              },
              { onSuccess: () => resolve(), onError: reject },
            );
          });
        }
      }

      for (const orig of customLinks) {
        if (!myCustomLinks.find((c) => c.id === orig.id)) {
          await new Promise<void>((resolve, reject) => {
            deleteLink.mutate(
              { id: orig.id },
              { onSuccess: () => resolve(), onError: reject },
            );
          });
        }
      }

      await queryClient.invalidateQueries({
        queryKey: getGetMyProfileQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetMyLinksQueryKey(),
      });
      toast({
        title: "Profile Saved ✓",
        description: "All changes have been saved.",
      });
    } catch (err: any) {
      console.error("Save failed:", err);
      toast({
        title: "Save Failed",
        description: err?.message || "Some changes could not be saved.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addCustomLink = () => {
    if (myCustomLinks.length >= customLinkLimit) {
      toast({
        title: "Link limit reached",
        description: `Your plan allows ${customLinkLimit} custom link${customLinkLimit > 1 ? "s" : ""}. Upgrade to add more.`,
        variant: "destructive",
      });
      return;
    }
    setMyCustomLinks([...myCustomLinks, { label: "", url: "", isNew: true }]);
  };

  const selectedTypeObj = CARD_PROFILE_TYPES.find((t) => t.id === selectedType);

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider">My Profile</h2>
          <p className="text-muted-foreground text-xs mt-1">
            Update your public profile and all your links in one place.
          </p>
        </div>
        <Button
          onClick={handleSaveAll}
          disabled={isSaving}
          className="px-4 py-2 rounded-xl font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:-translate-y-0.5 transition-all text-xs"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save All Changes
        </Button>
      </div>

      {/* ── Profile Info ── */}
      <section className="bg-background rounded-2xl border border-border/60 p-4 space-y-3">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-7 h-7 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <User className="w-4 h-4" />
          </div>
          <h3 className="text-xs font-semibold uppercase tracking-wider">Profile Info</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5 flex items-center gap-1.5">
              Display Name *
              <Tooltip text="Your name or business name shown at the top of your profile." />
            </label>
            <Input
              {...profileForm.register("name")}
              className="rounded-xl bg-background border-2 border-border focus:border-primary text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Avatar</label>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0 border-2 border-border">
                {profileForm.watch("avatarUrl") ? (
                  <img
                    src={profileForm.watch("avatarUrl")!}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-semibold text-primary">
                    {(profile.name ?? "U").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <label className="flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 border-dashed border-primary/40 text-primary text-xs font-medium hover:bg-primary/5 cursor-pointer transition-colors">
                  <Upload className="w-4 h-4" />
                  Upload Photo
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append("avatar", file);
                      try {
                        const base = import.meta.env.BASE_URL.replace(
                          /\/$/,
                          "",
                        );
                        const res = await fetch(
                          `${base}/api/v1/profile/avatar`,
                          {
                            method: "POST",
                            headers: {
                              Authorization: `Bearer ${localStorage.getItem("commonqr_token") ?? ""}`,
                            },
                            body: formData,
                          },
                        );
                        if (!res.ok) throw new Error("Upload failed");
                        const { avatarUrl } = await res.json();
                        profileForm.setValue("avatarUrl", avatarUrl);
                        toast({
                          title: "Avatar uploaded ✓",
                          description: "Your photo has been updated.",
                        });
                      } catch {
                        toast({
                          title: "Upload failed",
                          description: "Please try again.",
                          variant: "destructive",
                        });
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5 flex items-center gap-1.5">
            Bio / Tagline
            <Tooltip text="Keep it under 100 characters. Tell visitors what you do or offer in one sentence." />
          </label>
          <textarea
            {...profileForm.register("bio")}
            rows={1}
            placeholder="Tell your audience what you do..."
            className="w-full rounded-xl bg-background border-2 border-border focus:border-primary focus:ring-2 focus:ring-primary/10 px-4 py-2.5 outline-none transition-all resize-none text-xs"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5">
              Public Email
            </label>
            <Input
              {...profileForm.register("email")}
              type="email"
              placeholder="contact@domain.com"
              className="rounded-xl bg-background border-2 border-border focus:border-primary text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Location</label>
            <Input
              {...profileForm.register("location")}
              placeholder="Dubai, UAE"
              className="rounded-xl bg-background border-2 border-border focus:border-primary text-xs"
            />
          </div>
        </div>
      </section>


      {/* ── Profile Type ── */}
      <section className="bg-background rounded-2xl border border-border/60 p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-7 h-7 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider">Profile Type</h3>
            <p className="text-xs text-muted-foreground">
              Select your profile type to get a recommended link order. You have
              full freedom to customise your own template.
            </p>
          </div>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setTypeOpen(!typeOpen)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-border hover:border-primary bg-background transition-all text-left text-xs"
          >
            <span
              className={
                selectedTypeObj ? "font-medium" : "text-muted-foreground"
              }
            >
              {selectedTypeObj
                ? `${selectedTypeObj.emoji} ${selectedTypeObj.label}`
                : "Select your profile type…"}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground transition-transform ${typeOpen ? "rotate-180" : ""}`}
            />
          </button>
          {typeOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-card border border-border rounded-2xl shadow-2xl p-2 grid grid-cols-2 gap-1 max-h-72 overflow-y-auto">
              {CARD_PROFILE_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyProfileType(t.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium text-left transition-all hover:bg-primary/5 hover:text-primary ${selectedType === t.id ? "bg-primary/10 text-primary" : ""}`}
                >
                  <span className="text-lg">{t.emoji}</span> {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedTypeObj && (
          <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 text-primary" />
            Recommended order applied:{" "}
            {selectedTypeObj.order
              .slice(0, 4)
              .map((id) => BUILTIN_PLATFORMS.find((p) => p.id === id)?.name)
              .filter(Boolean)
              .join(" → ")}
            {selectedTypeObj.order.length > 4 && (<span title={selectedTypeObj.order.map((id) => BUILTIN_PLATFORMS.find((p) => p.id === id)?.name).filter(Boolean).join(" → ")} className="underline decoration-dotted cursor-help whitespace-nowrap">+{selectedTypeObj.order.length - 4} more</span>)}
          </p>
        )}
      </section>

      {/* ── Social & Platform Links ── */}
      <section className="bg-background rounded-2xl border border-border/60 p-4 space-y-2">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-7 h-7 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Globe className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider">Social &amp; Platform Links</h3>
              <Tooltip text="Links appear on your public profile in the order shown here. Drag the grip handle to reorder. Leave blank to hide." />
            </div>
            <p className="text-xs text-muted-foreground">
              Leave blank to hide from your profile. Drag{" "}
              <GripVertical className="inline w-3 h-3" /> to reorder.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {orderedPlatforms.map((platform, i) => {
            const Icon = platform.icon;
            return (
              <div
                key={platform.id}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 group transition-all ${dragIdx === i ? "opacity-40 scale-95" : ""}`}
              >
                <div className="cursor-grab active:cursor-grabbing text-muted-foreground/40 group-hover:text-muted-foreground transition-colors shrink-0">
                  <GripVertical className="w-4 h-4" />
                </div>
                <div
                  className={`w-7 h-7 rounded-xl flex items-center justify-center text-white shrink-0 ${platform.color}`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <span className="w-28 shrink-0 text-xs font-medium text-muted-foreground hidden sm:block">
                  {platform.name}
                </span>
                <div className="flex-1">
                  {platform.isPhone ? (
                    <><PhoneInput
                      value={platformUrls[platform.id] || ""}
                      onChange={(val) =>
                        setPlatformUrls({ ...platformUrls, [platform.id]: val })
                      }
                      platform={platform.id as "phone" | "whatsapp"}
                    />{platform.id === "whatsapp" && platformUrls.phone && platformUrls.whatsapp !== platformUrls.phone && (<button type="button" onClick={() => setPlatformUrls({ ...platformUrls, whatsapp: platformUrls.phone })} className="mt-1 text-[11px] text-primary hover:underline">Same as phone</button>)}</>
                  ) : (
                    <Input
                      value={platformUrls[platform.id] || ""}
                      onChange={(e) =>
                        setPlatformUrls({
                          ...platformUrls,
                          [platform.id]: e.target.value,
                        })
                      }
                      placeholder={(platform as any).placeholder || ""}
                      className="rounded-xl bg-background border-2 border-border focus:border-primary text-xs"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Custom Links ── */}
      <section className="bg-background rounded-2xl border border-border/60 p-4 space-y-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
              <Plus className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider">Custom Links</h3>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                {profile.currentPlan === "pro_plus" ||
                profile.currentPlan === "enterprise" ||
                profile.currentPlan === "ent_plus" ? (
                  <>
                    <Star className="w-3 h-3 text-amber-500" /> Pro Plus: up to
                    30 custom links
                  </>
                ) : profile.currentPlan === "pro" ? (
                  <>
                    <Zap className="w-3 h-3 text-violet-500" /> Pro: up to 10
                    custom links
                  </>
                ) : (
                  <>
                    Free: 3 custom links ·{" "}
                    <span className="text-primary font-medium">
                      Upgrade for more
                    </span>
                  </>
                )}
                <span className="text-muted-foreground/50">
                  ({myCustomLinks.length}/{customLinkLimit} used)
                </span>
              </p>
            </div>
          </div>
          {myCustomLinks.length < customLinkLimit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCustomLink}
              className="rounded-xl border-2 border-dashed border-primary/40 text-primary hover:bg-primary/5"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Link
            </Button>
          )}
        </div>

        {myCustomLinks.length === 0 ? (
          <div className="text-center py-8 rounded-xl border-2 border-dashed border-border text-muted-foreground text-xs">
            No custom links yet.{" "}
            <button
              onClick={addCustomLink}
              className="text-primary font-medium hover:underline"
            >
              + Add one
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {myCustomLinks.map((custom, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="flex-1 grid sm:grid-cols-2 gap-2">
                  <Input
                    value={custom.label}
                    onChange={(e) =>
                      setMyCustomLinks(
                        myCustomLinks.map((c, i) =>
                          i === idx ? { ...c, label: e.target.value } : c,
                        ),
                      )
                    }
                    placeholder="Label (e.g. My Menu)"
                    className="rounded-xl bg-background border-2 border-border focus:border-primary text-xs"
                  />
                  <Input
                    value={custom.url}
                    onChange={(e) =>
                      setMyCustomLinks(
                        myCustomLinks.map((c, i) =>
                          i === idx ? { ...c, url: e.target.value } : c,
                        ),
                      )
                    }
                    placeholder="https://..."
                    className="rounded-xl bg-background border-2 border-border focus:border-primary text-xs"
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setMyCustomLinks(myCustomLinks.filter((_, i) => i !== idx))
                  }
                  className="p-2 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Multiple Profiles ── */}
      <section className="bg-primary/5 rounded-2xl border border-primary/20 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
              <User className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                Multiple Profiles
                <span className="text-xs bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full">
                  Pro
                </span>
              </h3>
              <p className="text-xs text-muted-foreground">
                Manage separate profiles for different businesses or roles from
                one account.
              </p>
            </div>
          </div>
          <button
            className="px-4 py-2 rounded-xl border-2 border-indigo-200 text-indigo-600 text-xs font-semibold uppercase tracking-wider hover:bg-indigo-50 transition-colors"
            disabled
          >
            Coming Soon
          </button>
        </div>
      </section>

      {/* Bottom save */}
      <div className="flex justify-end pb-4">
        <Button
          onClick={handleSaveAll}
          disabled={isSaving}
          className="px-8 py-3 rounded-xl font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save All Changes
        </Button>
      </div>
    </div>
  );
}
